;; Copyright (C) 2016-2017, Doctor Evidence. All rights reserved.

(ns dre.search-form.render
  "Pills input with autocomplete component for search query."
  {:authors ["Michiel Borkent" "Joel Kuiper" "Jozef Wagner"]}
  (:require #?(:cljs [re-frame.core :refer [dispatch]])
            [clj-commons.humanize.inflect :refer [pluralize-noun]]
            [clojure.string :as str]
            [cuerdas.core :as cs]
            [dre.app.invert-color :refer [invert-color]]
            [dre.shared.interface.categories :as categories]
            [dre.shared.interface.search.pills :as pills]))

(defn default-icon-provider
  ([icon]
   (default-icon-provider icon nil))
  ([icon opts]
   (or
    (get {:jigsaw-left-grey-outline [:img {:src "/img/jigsaw-left-grey-outline.svg"}]
          :jigsaw-right-grey-outline [:img {:src "/img/jigsaw-right-grey-outline.svg"}]
          :jigsaw-left-blue [:img {:src "/img/jigsaw-left-blue.svg"}]
          :jigsaw-right-blue [:img {:src "/img/jigsaw-right-blue.svg"}]
          :jigsaw-right-umbrella [:img {:src "/img/jigsaw-right-umbrella.svg"}]
          :jigsaw-left-umbrella [:img {:src "/img/jigsaw-left-umbrella.svg"}]
          :jigsaw-right-white [:img {:src "/img/jigsaw-right-white.svg"}]
          :jigsaw-left-white [:img {:src "/img/jigsaw-left-white.svg"}]}
         icon)
    [:i.fa (merge {:class icon}
                  opts)])))

;;;; Implementation details

(def syntax-near-prefix-label-map
  (->> (range 1 100)
       (map #(vector (str "ppmnear-" %) (str "Near " % (pluralize-noun % " word"))))
       (into {})))

(def syntax-label-map
  (merge
   {"l" "("
    "r" ")"
    "same-page" "SAME PAGE"
    "disambiguate" "Disambiguated by"
    ;;"ppmbrandnames" "Brand names of"
    "ppmdescendants" "Descendants of"
    "ppmshallowdescendants" "Shallow descendants of"
    "ppmancestors" "Ancestors of"
    "ppmtreatments" "May treat"
    ;;"ppmmoas" "Mechanisms of action of"
    "ppmmanufacturersfor" "Manufacturers for"
    "ppmlabellersfor" "Labellers for"
    "ppmpackagersfor" "Packagers for"
    "ppmmanufacturedby" "Manufactured by"
    "ppmlabelledby" "Labelled by"
    "ppmpackagedby" "Packaged by"
    "ppmcauses" "Causative agents of"
    "ppmhastargetsof" "Has targets of"
    "ppmtargetsof" "Targets of"
    "ppmhaspathwayof" "Has pathway of"
    "ppmpathwayof" "Pathway of"
    "ppmdrugclass" "Drug class"
    "ppmcustom" "Custom"
    "ppmnoexplode" "Without descendants"
    "ppmwithbrandnames" "With brand names"
    "ppmwithgenericnames" "With generic names"
    "ppmanydrugnomenclatureof" "Any drug nomenclature of"

    "ststitle" "in title"
    "stsaffiliation" "in affiliation"
    "stscriteria" "in trial criteria"
    "stsinclusion-criteria" "in trial inclusion criteria"
    "stsexclusion-criteria" "in trial exclusion criteria"
    "stsabstract" "in abstract"
    "stsjournal" "in journal"
    "stssponsor" "in sponsors"
    "stsstudy-center" "in study center"
    "stsinterventions" "in interventions"
    "stsoutcomes" "in outcomes"
    "stsconditions" "in conditions"
    "ststitle-or-abstract" "in title/abstract"
    "ststitle-or-abstract-or-keywords" "in title/abstract/keywords"
    "stskeywords" "in keywords"
    "ststitle-or-keywords" "in title/keywords"

    "stsbiomedicalrelationships" "in biomedical relationships"
    "stscongress" "in congress"
    "bs" "Boost"
    "stspresented" "presented"}
    syntax-near-prefix-label-map))


(defn syntax-label
  [pill]
  (let [{:keys [:pill/query :pill/label :pill/args]} pill
        query (str/lower-case query)
        label (or label
                  (when query
                    (or (syntax-label-map query) (str/upper-case query))))]
    (if args (str label " (" args ")") label)))

(defn prefix-mode-pill?
  [pill]
  (let [{:keys [:pill/query :pill/type]} pill]
    (and (= :syntax type)
         (not (str/blank? query))
         (str/starts-with? (str/lower-case query) "ppm"))))

(defn prefix-near-mode-pill?
  [pill]
  (let [{:keys [:pill/query :pill/type]} pill]
    (and (= :syntax type)
         (not (str/blank? query))
         (str/starts-with? (str/lower-case query) "ppmnear"))))

(defn suffix-mode-pill?
  [pill]
  (let [{:keys [:pill/query :pill/type]} pill]
    (and (= :syntax type)
         (not (str/blank? query))
         (str/starts-with? (str/lower-case query) "sts"))))

(def tag-icons
  {"company" :fa-industry
   "country" :fa-globe
   "study-center" :fa-flask
   "gender" :fa-user
   "age" :fa-user
   "race" :fa-user
   "predicted" :fa-magic
   "ethnicity" :fa-user})

(def tag-suffix
  {"study-center" "study center"})

(def reldate-types
  #{\m \d \y \w})

(defn relative-date?
  [s]
  (reldate-types (first s)))

(defn parse-int
  [s]
  #?(:cljs (js/parseInt s)
     :clj (try (Integer/parseInt s)
               (catch Exception _e nil))))

(defn nicer-date
  [s]
  (if (pos? (count s))
    (let [unit (first s)
          amount (when (< 1 (count s)) (parse-int (subs s 1)))
          plural? (and amount (< 1 amount))
          rt (case unit
               \m "month"
               \d "day"
               \y "year"
               \w "week"
               \n "today"
               nil)]
      (cond (nil? rt) s
            plural? (str amount " " rt "s")
            :else (str rt)))
    s))

(defn truncate
  [text max-len & {:as opts}]
  (if (< max-len (count text))
    (str (subs text 0 max-len) "…" (:hint opts))
    text))

(defn hiccup->text [hiccup]
  (let [ret (cond (string? hiccup) [(str/trim hiccup)]
                  (sequential? hiccup) (not-empty (keep hiccup->text hiccup)))]
    (when ret (str/join " " ret))))

(defn single-year?
  [start end]
  (try
    (and (= 4 (count start))
         (= 4 (count end))
         (= 1 (- (parse-int end) (parse-int start))))
    #?(:cljs (catch :default _ nil)
       :clj (catch Exception _ nil))))

(defn render-range-pill
  ([pill]
   (render-range-pill pill default-icon-provider))
  ([pill icon-provider]
   (let [pill (update pill :pill/field #(if (string? %) (keyword %) %))]
     [:div.item.label.pill.arange
      (if (single-year?
           (:pill/start pill) (:pill/end pill))
        [:span (icon-provider :fa-clock-o) #_[:i.fa.fa-clock-o]
         (condp = (:pill/field pill)
           :changed [:i " last changed "]
           :published [:i " published "]
           :approved [:i " approved "]
           :issued [:i " issued "]
           :patent_expire_date [:i " patent expires "]
           :submission_date [:i " submitted "]
           :exclusivity_expiration_date [:i " exclusivity expires "]
           :orphan_exclusivity_expiration_date [:i " exclusivity expires "])
         [:i " in year "]
         (nicer-date (:pill/start pill))]
        [:span (icon-provider :fa-clock-o) #_[:i.fa.fa-clock-o]
         (condp = (:pill/field pill)
           :changed [:i " last changed "]
           :published [:i " published "]
           :approved [:i " approved "]
           :issued [:i " issued "]
           :patent_expire_date [:i " patent expires "]
           :submission_date [:i " submitted "]
           :exclusivity_expiration_date [:i " exclusivity expires "]
           :orphan_exclusivity_expiration_date [:i " exclusivity expires "])
         (when-let [s (:pill/start pill)]
           [:i (if (relative-date? s) " past " " from ")])
         (nicer-date (:pill/start pill))
         (when-let [s (:pill/end pill)]
           [:i (if (relative-date? s) " past " " through ")])
         (nicer-date (:pill/end pill))])])))


(defn flat-div
  "Returns a component that displays a given flat pill. "
  [pill prevpill nextpill opts]
  (let [{:keys [:pill/type :pill/query :pill/label
                :pill/definitions :pill/canonical-label
                :pill/pills]} pill
        type (if (string? type)
               (keyword type)
               type)
        type (if (or (str/starts-with? (name type) "regdoc-")
                     (str/starts-with? (name type) "guidance-"))
               :regdoc
               type)
        definitions (str
                     canonical-label
                     (when (seq definitions)
                       (str
                        "\n\n"
                        (truncate
                         (cs/strip-tags (str/join "\n\n" (distinct definitions)))
                         350
                         :hint " [click for more information]"))))
        alabel label
        label (or label query)
        {:keys [:source-labels
                :user-profile
                :tags
                :excluded
                #?(:cljs :no-folds)
                #?(:cljs :close-current-fold)
                icon-provider]
         :or {icon-provider default-icon-provider}} opts
        uuid->tag (zipmap (map (comp str :tag_uuid) tags)
                          tags)
        tag-labels (zipmap (map (comp str :tag_uuid) tags)
                           (map :name tags))
        tag-owners (zipmap (map (comp str :tag_uuid) tags)
                           (map :owner tags))]
    (case type
      :text
      [:div
       {:class
        (str/join " " ["item" "label" "pill" "freetext"
                       (when (prefix-near-mode-pill? prevpill) "ppm2")
                       (when (suffix-mode-pill? nextpill) "sts2")
                       (when (= :ambiguous (:pill/category pill))
                         "ambiguous")])}
       (when (prefix-near-mode-pill? prevpill)
         [:span.before (icon-provider :jigsaw-right-grey-outline)])
       (when (suffix-mode-pill? nextpill)
         [:span.after (icon-provider :jigsaw-left-grey-outline)])
       (cond
         (= :ambiguous (:pill/category pill))
         [:span
          {:title "Term is ambiguous or no longer present in our dictionaries"}
          (icon-provider :fa-warning)
          #_[:i.fa.fa-warning
             {:style #?(:clj "color: #700"
                        :cljs {:color "#700"})}]
          [:span.label-text [:span (str " " label)]]]
         :else
         [:span.label-text [:span label]])]
      :sparql
      [:div
       {:class
        (str/join " " ["item" "label" "pill" "freetext"])}
       [:span label]]
      :source
      [:div.item.label.pill.category
       [:span (cond
                (str/starts-with? query "http") (icon-provider :fa-rss) #_[:i.fa.fa-rss]
                (str/starts-with? query "doc-congress") (icon-provider :fa-file-text-o) #_[:i.fa.fa-file-text-o]
                :else (icon-provider :fa-inbox) #_[:i.fa.fa-inbox])
        (str " " (or alabel (get source-labels query query)))]]
      :category
      [:div.item.label.pill.category
       [:span (icon-provider :fa-inbox) #_[:i.fa.fa-inbox]
        (str " " (or (:label (categories/category-map
                              (:pill/query pill)))
                     (when-let [q (:pill/query pill)]
                       (str/capitalize q))))]]
      (:signal :signal/unresolved)
      [:div
       {:class
        (cond
          (suffix-mode-pill? nextpill)
          "item label pill label-primary sts2 signal"
          :else
          "item label pill label-primary signal")}
       [:span
        (when (suffix-mode-pill? nextpill)
          [:span.after (icon-provider :jigsaw-left-blue)])
        [:span (icon-provider :fa-save) #_[:i.fa.fa-save]
         (if (= :signal type)
           (str " " alabel)
           [:span
            {:title "Unresolved saved search reference."}
            (icon-provider :fa-warning)
            #_[:i.fa.fa-warning
               {:style {:color "#700"}}]])]]]
      :tag
      (let [tn (namespace (keyword query))
            icon (or (tag-icons tn) :fa-hashtag)
            suffix (tag-suffix tn)
            tc (if (str/blank? tn)
                 "atag"
                 (str "atag.a" tn))]
        [(keyword (str "div.item.label.pill." tc))
         [:span
          (icon-provider icon)
          #_[(keyword (str "i.fa." icon))] (str " " label
                                                (when suffix
                                                  (str " (" suffix ")")))]])
      :utag
      (let [#_:clj-kondo/ignore tag (get uuid->tag query)]
        [:div.item.label.pill.utag
         (when-let [color (not-empty (:color tag))]
           {:style {:color (invert-color color true)
                    :background-color color}})
         [:span
          #?(:cljs (icon-provider :fa-hashtag
                                  (when-let [color (:color tag)]
                                    {:style {:color (invert-color color true)}}))
             :clj "#")
          [:span
           (when-let [owner (or (get tag-owners query)
                                (:pill/owner pill))]
             (when-not (= owner (get user-profile :user-id))
               [:span
                #?(:cljs (icon-provider :fa-user
                                        {:title owner})
                   :clj (str (.appendCodePoint
                              (StringBuilder.)
                              0x1F464)))]))
           (or (:pill/label pill)
               (get tag-labels query)
               (icon-provider :fa-warning
                              {:title "Unresolved tag"
                               :style {:color "rgb(119, 0, 0)"}}))]]])
      :term
      [:div
       {:title definitions
        :class
        (let [class (cond
                      (and (prefix-mode-pill? prevpill) (suffix-mode-pill? nextpill))
                      "item label pill label-primary ppm2 sts2"
                      (prefix-mode-pill? prevpill)
                      "item label pill label-primary ppm2"
                      (suffix-mode-pill? nextpill)
                      "item label pill label-primary sts2"
                      :else
                      "item label pill label-primary")
              class (if excluded (str "excluded " class) class)]
          #?(:clj class
             :cljs (if no-folds class
                       (str class " selectable"))))}
       [:a
        #?(:cljs
           (when-not no-folds
             {:on-click
              (fn [_]
                (let [pill (cond-> pill
                             (prefix-mode-pill? prevpill)
                             (assoc :pill/mode
                                    (subs (:pill/query prevpill) 3)))
                      fn-opts {:phrase [{:label label, :sense query}]
                               :pill pill
                               :type :term
                               :term {:query/pills [pill]
                                      :query/type :articles}}
                      default-fn #(dispatch [:dre.search.folds.core/toggle :concept %])
                      f (or (get opts :sense-on-click-fn)
                            default-fn)
                      f #(f fn-opts)]
                  (if (some? close-current-fold)
                    (do
                      (close-current-fold)
                      (js/setTimeout f 300)) ; wait for css transition to finish
                    (f))))}))
        (when (prefix-mode-pill? prevpill)
          [:span.before (icon-provider :jigsaw-right-blue)])
        (when (suffix-mode-pill? nextpill)
          [:span.after (icon-provider :jigsaw-left-blue)])
        (cond
          (= :company (:pill/category pill))
          [:span (icon-provider :fa-industry) #_[:i.fa.fa-industry]
           [:span.label-text [:span (str " " label)]]]
          (= :country (:pill/category pill))
          [:span (icon-provider :fa-globe) #_[:i.fa.fa-globe]
           [:span.label-text [:span (str " " label)]]]
          (= :age (:pill/category pill))
          [:span (icon-provider :fa-user) #_[:i.fa.fa-user]
           [:span.label-text [:span (str " " label)]]]
          (= :drugclass (:pill/category pill))
          [:span (icon-provider :fa-sitemap) #_[:i.fa.fa-sitemap]
           [:span.label-text [:span (str " " label)]]]
          (= :gender (:pill/category pill))
          [:span (icon-provider :fa-user) #_[:i.fa.fa-user]
           [:span.label-text [:span (str " " label)]]]
          :else
          [:span.label-text [:span label]])]]
      :senses
      [:div
       {:title (first (seq (:pill/definitions pill)))
        :class
        (cond
          (and (prefix-mode-pill? prevpill) (suffix-mode-pill? nextpill))
          "item label pill label-primary ppm2 sts2 senses"
          (prefix-mode-pill? prevpill)
          "item label pill label-primary ppm2 senses"
          (suffix-mode-pill? nextpill)
          "item label pill label-primary sts2 senses"
          :else
          "item label pill label-primary senses")}
       [:span
        (when (prefix-mode-pill? prevpill)
          [:span.before (icon-provider :jigsaw-right-umbrella)])
        (when (suffix-mode-pill? nextpill)
          [:span.after (icon-provider :jigsaw-left-umbrella)])
        [:span (icon-provider :fa-umbrella) #_[:i.fa.fa-umbrella]
         [:span.label-text [:span (str " " label)]]]]]
      :range
      (render-range-pill pill icon-provider)
      :study-size
      [:div.item.label.pill.astudysize
       [:span (icon-provider :fa-users) #_[:i.fa.fa-users]
        (let [start (:pill/start pill)
              end (:pill/end pill)]
          (if (and start end)
            (if (= start end)
              (str " study size = " start)
              (str " " start " \u2264 study size \u2264 " end))
            (str " study size \u2265 " start)))]]
      :syntax
      [:div
       {:class (cond
                 (and (prefix-mode-pill? pill) (suffix-mode-pill? pill))
                 "item label pill syntax ppm sts"
                 (prefix-mode-pill? pill)
                 "item label pill syntax ppm"
                 (suffix-mode-pill? pill)
                 "item label pill syntax sts"
                 (suffix-mode-pill? nextpill)
                 "item label pill syntax sts2"
                 :else
                 "item label pill syntax")}
       [:span
        (when (and
               (not= (:pill/type prevpill) :syntax)
               (suffix-mode-pill? pill))
          [:span.before (icon-provider :jigsaw-right-white)])
        [:span (syntax-label pill)]
        (when (prefix-mode-pill? pill)
          [:span.after (icon-provider :jigsaw-left-white)])]]
      :author-constrained
      [:div.item.label.pill.author
       [:span (icon-provider :fa-user-md) #_[:i.fa.fa-user-md]
        label
        " WITH "
        (let [constrain-label
              {:affiliation-constraint "aff"
               :coauthor-constraint "coauthor"
               :email-constraint "email"
               :id-constraint "id"}
              mf (fn [pill]
                   (let [label (constrain-label (:pill/type pill))]
                     (map #(str label "=" %) (:pill/values pill))))]
          (str/join ", " (mapcat mf pills)))]]
      :author
      [:div.item.label.pill.author
       [:span (icon-provider :fa-user-md) #_[:i.fa.fa-user-md] (str "AUTHOR: " label)]]
      :uris
      [:div.item.label.pill.source
       [:span (str/join ", " (:pill/uris pill))]]
      :ai-sum-uris
      [:div.item.label.pill.source
       [:span (str/join ", " (:pill/uris pill))]]
      
      ;; REGINTEL
      :regdoc
      [:div.item.label.pill.regdoc
       [:span (icon-provider :fa-filter) (:pill/query pill)]]
      :synonyms
      #?(:clj
         [:div.item.label.pill.synonyms
          [:span (icon-provider :fa-list) #_[:i.fa.fa-list] (:pill/query pill)]]
         :cljs
         [:div.item.label.pill.synonyms
          [:span (icon-provider :fa-list) #_[:i.fa.fa-list] (:pill/query pill)]
          [:div.synonyms-list
           [:ul
            (doall
             (for [{:keys [:use? :synonym]} (:pill/synonyms pill)]
               [:li {:key synonym
                     :class (when-not use? :not-used)}
                [:span synonym]]))]]])

      #_[:div
         (str "Unknown: " (pr-str pill))])))

;;;; Public API

(defn pills-bare
  "Process search-query and convert pills to hiccup structure"
  [search-query ctx]
  (let  [pills (pills/main-pills
                (pills/pills->flats
                 (:query/pills search-query)))]
    [:div
     (when (:indent ctx)
       " \u21B3 ")
     (doall
      (for [[#?(:clj _idx :cljs idx) pill
             prevpill nextpill]
            (map vector (range) pills (cons nil pills) (concat (rest pills) [nil]))]
        #?(:clj (flat-div pill prevpill nextpill ctx)
           :cljs ^{:key idx} [flat-div pill prevpill nextpill ctx])))]))
